import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Loader2 } from "lucide-react";
import { fmtDay, qty, type BoardColumn } from "@/types/production";
import { PriorityBadge } from "./ProductionBadges";

/** One column per process; each card is a work order currently at that process */
export function ProductionBoard({ reloadKey }: { reloadKey: number }) {
  const { error } = useToast();
  const [columns, setColumns] = useState<BoardColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEmpty, setShowEmpty] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get("/api/WorkOrder/Board")
      .then((r) => setColumns(unwrapObject<{ columns: BoardColumn[] }>(r).columns || []))
      .catch((err: any) => error("Fetch Error", err.message || "Failed to load the board"))
      .finally(() => setLoading(false));
  }, [reloadKey]);

  if (loading && !columns.length) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  const total = columns.reduce((n, c) => n + c.cards.length, 0);
  // Columns with nothing in them only take up room, so they are hidden unless asked for
  const shown = showEmpty || total === 0 ? columns : columns.filter((c) => c.cards.length > 0);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        {total === 0 ? <p className="text-sm text-muted-foreground">Nothing is in production right now. Make a work order to get started.</p> : <p className="text-xs text-muted-foreground">{total} work order{total === 1 ? "" : "s"} in production</p>}
        <label className="text-xs text-muted-foreground inline-flex items-center gap-2 shrink-0">
          <input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} /> Show empty stages
        </label>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-3">
        {shown.map((col) => (
          <div key={col.process_code} className="w-[270px] shrink-0 rounded-xl bg-muted/40 border border-border/60">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-border/60">
              <h3 className="text-sm font-bold">{col.process_name}</h3>
              <span className="text-xs font-semibold rounded-full bg-background border border-border px-2 py-0.5">{col.cards.length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[120px]">
              {col.cards.map((c) => (
                <Link key={c.id} to={`/production/${c.id}`} className={`block rounded-lg border bg-card p-3 shadow-xs hover:shadow-md hover:border-primary/40 transition ${c.late ? "border-rose-400/70" : "border-border"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <code className="font-mono text-xs font-semibold">{c.wo_number}</code>
                    <PriorityBadge priority={c.priority} />
                  </div>
                  <div className="mt-1 text-sm font-semibold leading-tight">{c.product_name}</div>
                  {c.variant_name && <div className="text-[11px] text-muted-foreground">{c.variant_name}</div>}
                  <div className="mt-1.5 text-xs">{qty(c.planned_qty, c.unit)}</div>
                  <div className="mt-1 text-[11px] text-muted-foreground">{c.order_number ? `${c.order_number}${c.account_name ? ` · ${c.account_name}` : ""}` : "For stock"}</div>
                  <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
                    <span className={c.stage_status === "IN_PROGRESS" ? "text-blue-600 font-medium" : "text-muted-foreground"}>{c.stage_status === "IN_PROGRESS" ? "Running" : "Waiting"}{c.job_worker ? ` · ${c.job_worker}` : ""}</span>
                    <span className={c.late ? "text-rose-600 font-semibold" : "text-muted-foreground"}>{c.late ? "Late · " : "Due "}{fmtDay(c.planned_end || c.due_date)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
